
import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from 'context/user'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import {
  LIVE_BUDGET_COPY,
  SETTABLE_SPEED_LEVELS,
  isSettableSpeedLevel,
  speedLevelLabel
} from 'components/admin/YouTubeSpeedPanel'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { EventYouTubeSpeed } from 'types/api'
import getEventYouTubeSpeedQuery from 'graphql/getEventYouTubeSpeed.graphql'
import setEventYouTubeSpeedMutation from 'graphql/setEventYouTubeSpeed.graphql'

const LOAD_ERROR_COPY = 'Couldn\'t load this stream\'s YouTube speed.'
const ENDED_COPY = 'This stream has ended: its YouTube speed can no longer change.'
const APP_DEFAULT = ''

export const YouTubeStreamSpeedControl = ({
  eventId,
  ended
}: {
  eventId: string
  ended: boolean
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [speed, setSpeed] = useState<EventYouTubeSpeed | null>(null)
  const [choice, setChoice] = useState<string>(APP_DEFAULT)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const inFlightRef = useRef(false)
  const aliveRef = useRef(true)
  useEffect(() => {
    aliveRef.current = true
    return () => { aliveRef.current = false }
  }, [])

  const eventIdRef = useRef(eventId)
  eventIdRef.current = eventId
  const adopt = (row: EventYouTubeSpeed | null): void => {
    if (row == null || (row.eventId != null && row.eventId !== eventIdRef.current)) return
    setSpeed(row)
    setChoice(isSettableSpeedLevel(row.level) ? row.level : APP_DEFAULT)
  }

  const load = useCallback(async (): Promise<void> => {
    if (auth0Token == null || eventId === '') return
    setLoading(true)
    setLoadError(null)
    try {
      const { speed: row } = await graphqlRequest<{ speed: EventYouTubeSpeed | null }>(
        getEventYouTubeSpeedQuery, { eventId }, auth0Token
      )
      if (!aliveRef.current) return
      if (row == null) setLoadError(LOAD_ERROR_COPY)
      else adopt(row)
    } catch {
      if (aliveRef.current) setLoadError(LOAD_ERROR_COPY)
    } finally {
      if (aliveRef.current) setLoading(false)
    }
  }, [auth0Token, eventId])

  useEffect(() => {
    void load()
  }, [load])

  const handleSave = async (): Promise<void> => {
    if (auth0Token == null || saving || loading || ended || inFlightRef.current) return
    inFlightRef.current = true
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      const { speed: row } = await graphqlRequest<{ speed: EventYouTubeSpeed | null }>(
        setEventYouTubeSpeedMutation,
        { eventId, level: choice === APP_DEFAULT ? null : choice },
        auth0Token
      )
      if (!aliveRef.current) return
      adopt(row)
      setSaved(true)
    } catch (err) {
      if (aliveRef.current) setSaveError(err instanceof Error ? err.message : 'Failed to save this stream\'s YouTube speed')
    } finally {
      inFlightRef.current = false
      if (aliveRef.current) setSaving(false)
    }
  }

  const current = speed != null && isSettableSpeedLevel(speed.level) ? speed.level : APP_DEFAULT
  const changed = choice !== current
  const liveBlocked = speed != null && speed.liveAllowed !== true

  return (
    <div
      data-testid='yt-stream-speed'
      className='mt-4 rounded-md border border-dashed border-white/15 bg-dm-bg-accent/20 p-3'
    >
      <div className='flex items-center gap-2'>
        <p className='text-sm font-medium text-dm-text-primary'>Read speed</p>
        <span className='rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300 ring-1 ring-inset ring-amber-500/30'>
          Staff only
        </span>
      </div>
      <p className='mt-0.5 text-[11px] leading-relaxed text-dm-text-accent'>
        How often this stream&apos;s YouTube chats are read. Only superusers see this.
      </p>

      {loadError != null && (
        <div className='mt-2 flex items-center gap-3 text-xs text-red-300' data-testid='yt-stream-speed-error'>
          <span>{loadError}</span>
          <button
            type='button'
            onClick={() => { void load() }}
            disabled={loading}
            data-testid='yt-stream-speed-retry'
            className='rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60'
          >
            Try again
          </button>
        </div>
      )}

      {loadError == null && speed == null && (
        <div className='mt-2 flex items-center py-1' data-testid='yt-stream-speed-loading'>
          <LoadingSpinner />
        </div>
      )}

      {speed != null && (
        <>
          <div className='mt-2 flex flex-col gap-2 sm:flex-row sm:items-center'>
            <label htmlFor={`yt-stream-speed-${eventId}`} className='sr-only'>Read speed for this stream</label>
            <select
              id={`yt-stream-speed-${eventId}`}
              value={choice}
              onChange={(e) => { setChoice(e.target.value); setSaved(false); setSaveError(null) }}
              disabled={saving || ended}
              data-testid='yt-stream-speed-select'
              className='flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'
            >
              <option value={APP_DEFAULT}>App default ({speedLevelLabel(speed.appDefaultLevel)})</option>
              {SETTABLE_SPEED_LEVELS.map((level) => (
                <option key={level} value={level} disabled={level === 'LIVE' && liveBlocked && current !== 'LIVE'}>
                  {speedLevelLabel(level)}
                </option>
              ))}
            </select>
            <button
              type='button'
              onClick={() => { void handleSave() }}
              disabled={saving || loading || ended || !changed}
              data-testid='yt-stream-speed-save'
              className='inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-xs font-semibold text-dm-text-primary hover:border-white/20 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {saving && <LoadingSpinner />}
              Save
            </button>
          </div>
          {liveBlocked && (
            <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='yt-stream-speed-live-note'>{LIVE_BUDGET_COPY}</p>
          )}
          <p className='mt-2 text-[11px] text-dm-text-secondary' data-testid='yt-stream-speed-effective'>
            Effective now: {speedLevelLabel(speed.effectiveLevel)}.
            {speed.brakeCapLevel != null && ` The budget brake caps every YouTube chat at ${speedLevelLabel(speed.brakeCapLevel)} today.`}
          </p>
          {speed.level != null && speed.updatedAt != null && (
            <p className='mt-0.5 text-[11px] text-dm-text-accent' data-testid='yt-stream-speed-updated'>
              Set for this stream at {formatLocalDateTime(speed.updatedAt)}.
            </p>
          )}
          {ended && (
            <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='yt-stream-speed-ended'>{ENDED_COPY}</p>
          )}
          {saved && !changed && (
            <p className='mt-1 text-[11px] text-emerald-300' data-testid='yt-stream-speed-saved'>Saved.</p>
          )}
        </>
      )}

      {saveError != null && (
        <div className='mt-2' data-testid='yt-stream-speed-save-error'>
          <ErrorInline onDismiss={() => setSaveError(null)}>{saveError}</ErrorInline>
        </div>
      )}
    </div>
  )
}
